import {useEffect, useReducer, useState} from "react"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Container from "@mui/material/Container"
import FormControlLabel from "@mui/material/FormControlLabel"
import Grid from "@mui/material/Grid"
import List from "@mui/material/List"
import Switch from "@mui/material/Switch"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import {init, reducer} from "../utils/reducer.js"
import {defaultFeeds, defaultGroups} from "../utils/defaultFeeds.js"
import Feed from "./Feed"

// TODO: Display a filtered list using the search bar.
const FeedList = ({user, code, groups, showGroupList, appFeeds}) => {
  const [groupName, setGroupName] = useState("")
  const [showCount, setShowCount] = useState(true)
  const [selected, setSelected] = useState([])
  const [message, setMessage] = useState("")
  const [disabledButton, setDisabledButton] = useState(false)
  const [hideDefaultFeeds, setHideDefaultFeeds] = useState(false)
  const [feeds, updateFeed] = useReducer(reducer(), init)

  useEffect(() => {
    for (const feed of defaultFeeds) {
      updateFeed(feed)
    }
  }, [])

  // Populate from already-loaded app feeds. App.jsx stores the full metadata
  // (title, image, description, language, defaultGroup) so no separate Holster
  // listener is needed here.
  useEffect(() => {
    for (const [url, feed] of Object.entries(appFeeds || {})) {
      if (!feed || !feed.title) continue
      updateFeed({
        key: url,
        title: feed.title,
        image: feed.image || "",
        description: feed.description || "",
        html_url: feed.url || "",
        language: feed.language || "",
        defaultGroup: feed.defaultGroup || "",
      })
    }
  }, [appFeeds])

  useEffect(() => {
    if (!user) return

    user
      .get("public")
      .next("settings")
      .next("hideDefaultFeeds", val => setHideDefaultFeeds(val))
  }, [user])

  const dismissDefaults = () => {
    user
      .get("public")
      .next("settings")
      .next("hideDefaultFeeds")
      .put(true, err => {
        if (err) {
          console.error(err)
          return
        }

        setHideDefaultFeeds(true)
      })
  }

  const selectFeed = feed => {
    if (selected.includes(feed.key)) {
      setSelected(selected.filter(key => key !== feed.key))
    } else {
      setSelected([...selected, feed.key])
    }
  }

  const allDefaultFeedsAdded = () => {
    const defaultFeedKeys = feeds.all
      .filter(f => f.defaultGroup)
      .map(f => f.key)
    return (
      defaultFeedKeys.length > 0 &&
      defaultFeedKeys.every(key =>
        feeds.all.some(f => f.key === key && !f.defaultGroup),
      )
    )
  }

  const createGroup = async () => {
    if (!groupName) {
      setDisabledButton(false)
      setMessage("Group name required")
      return
    }

    setDisabledButton(true)
    setMessage("Creating group...")

    // Convert selected feeds to an object to store in Holster.
    // See Display useEffect which converts back to an array.
    let f = {}
    for (let i = 0; i < selected.length; i++) {
      const url = selected[i]
      const feed = feeds.all.find(current => current.key === url)
      if (!feed) continue

      f[url] = true
      // Store each feed for the user to listen for updates in App.js.
      const err = await new Promise(res => {
        user.get("public").next("feeds").next(url).put(feed, res)
      })
      if (err) console.error(err)
    }
    const group = {
      name: groupName,
      showCount,
      feeds: f,
      count: 0,
      latest: Date.now(),
      text: "",
      author: "",
      timestamp: Date.now(),
    }

    user
      .get("public")
      .next("groups")
      .put(group, true, err => {
        if (err) {
          setDisabledButton(false)
          setMessage("Could not create group")
          console.error(err)
        } else {
          showGroupList(true)
        }
      })
  }

  return (
    <Container maxWidth="md">
      <Grid container>
        <Grid item xs={12}>
          <Card sx={{mt: 2}}>
            <CardContent>
              <Typography variant="h5">New group</Typography>
              <TextField
                id="feedlist-new-group"
                label="Group name"
                variant="outlined"
                fullWidth={true}
                margin="normal"
                value={groupName}
                onChange={event => setGroupName(event.target.value)}
              />
              <FormControlLabel
                sx={{display: "block", mt: 1}}
                control={
                  <Switch
                    checked={showCount}
                    onChange={e => setShowCount(e.target.checked)}
                  />
                }
                label="Show unread count"
              />
              <Button
                sx={{mt: 1}}
                variant="contained"
                disabled={
                  selected.length === 0 || groupName === "" || disabledButton
                }
                onClick={createGroup}
              >
                Submit
              </Button>
              {selected.length > 0 && !message && (
                <Typography sx={{m: 1}} variant="string">
                  {`${selected.length} feed${selected.length > 1 ? "s" : ""} selected`}
                </Typography>
              )}
              {message && (
                <Typography sx={{m: 1}} variant="string">
                  {message}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <List>
            {feeds &&
              feeds.all.map(
                f =>
                  f.title &&
                  !f.defaultGroup && (
                    <Feed
                      key={f.key}
                      user={user}
                      code={code}
                      groups={groups}
                      feed={f}
                      selected={selected.includes(f.key)}
                      selectFeed={selectFeed}
                    />
                  ),
              )}
          </List>
        </Grid>
        {!hideDefaultFeeds && !allDefaultFeedsAdded() && (
          <Grid item xs={12}>
            <Typography>
              Looking for feeds? Click add feed in the menu, or try some of the
              options below.
            </Typography>
          </Grid>
        )}
        {!hideDefaultFeeds &&
          defaultGroups.map(
            defaultGroup =>
              feeds &&
              feeds.all.filter(f => f.title && f.defaultGroup === defaultGroup)
                .length > 0 && (
                <Grid item xs={12} key={defaultGroup}>
                  <Typography variant="h6">{defaultGroup}</Typography>
                  <List>
                    {feeds &&
                      feeds.all.map(
                        f =>
                          f.title &&
                          f.defaultGroup === defaultGroup && (
                            <Feed
                              key={f.key}
                              user={user}
                              code={code}
                              groups={groups}
                              feed={f}
                              selected={selected.includes(f.key)}
                              selectFeed={selectFeed}
                            />
                          ),
                      )}
                  </List>
                </Grid>
              ),
          )}
        {!hideDefaultFeeds && !allDefaultFeedsAdded() && (
          <Grid item xs={12}>
            <Button sx={{mt: 1}} variant="contained" onClick={dismissDefaults}>
              Dismiss Defaults
            </Button>
          </Grid>
        )}
      </Grid>
    </Container>
  )
}

export default FeedList
